import React, { useRef, useState, useEffect } from 'react';
import { parseVerilog, parseYosysJson } from './astParser';

const PnRVisualizer = ({ parsedGraph: parentGraph, theme, code }) => {
  const canvasRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 40, y: 40 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [hoveredCell, setHoveredCell] = useState(null);
  const [localGraph, setLocalGraph] = useState(null);

  // Self-parse code if parent didn't provide parsedGraph
  useEffect(() => {
    if (!parentGraph && code) {
      try {
        let graph;
        if (code.trim().startsWith('{')) {
          const jsonObj = JSON.parse(code);
          graph = parseYosysJson(jsonObj);
        } else {
          graph = parseVerilog(code);
        }
        if (graph) {
          setLocalGraph(graph);
        }
      } catch (err) {
        console.error("PnR auto-parse error:", err);
      }
    }
  }, [code, parentGraph]);

  const activeGraph = parentGraph || localGraph || { inputs: [], outputs: [], wires: [], gates: [] };
  const gatesList = activeGraph.gates || [];
  const inputsList = activeGraph.inputs || [];
  const outputsList = activeGraph.outputs || [];

  // Generate cell footprints dynamically
  const cells = gatesList.map((gate, index) => {
    const cellsPerRow = 4;
    const col = index % cellsPerRow;
    const row = Math.floor(index / cellsPerRow);

    const cellWidth = gate.type.includes('XOR') ? 60 : 50;
    const cellHeight = 80;
    const cellX = 80 + col * 120;
    const cellY = 60 + row * 140;

    return {
      id: gate.id,
      name: gate.name,
      type: gate.type,
      x: cellX,
      y: cellY,
      w: cellWidth,
      h: cellHeight,
      inputs: gate.inputs,
      outputs: gate.outputs
    };
  });

  // Generate routing connections dynamically
  const wires = [];
  
  // 1. Connect Inputs to Cell Inputs
  inputsList.forEach((inp) => {
    const consumers = cells.filter(c => c.inputs.includes(inp));
    consumers.forEach((c) => {
      const pinIdx = c.inputs.indexOf(inp);
      const pinY = c.y + (pinIdx === 0 ? 25 : 55);
      wires.push({
        path: [
          { x: 20, y: c.y + 40 }, 
          { x: c.x - 20, y: c.y + 40 },
          { x: c.x - 20, y: pinY },
          { x: c.x, y: pinY }
        ],
        layer: 'M1',
        label: inp
      });
    });
  });

  // 2. Connect Internets (outputs of cells to inputs of other cells)
  cells.forEach((producer) => {
    const outNet = producer.outputs[0];
    const consumers = cells.filter(c => c.inputs.includes(outNet));
    
    consumers.forEach((c) => {
      const pinIdx = c.inputs.indexOf(outNet);
      const pinY = c.y + (pinIdx === 0 ? 25 : 55);
      const startX = producer.x + producer.w;
      const startY = producer.y + 40;
      
      wires.push({
        path: [
          { x: startX, y: startY },
          { x: startX + 15, y: startY },
          { x: startX + 15, y: pinY },
          { x: c.x, y: pinY }
        ],
        layer: 'M2',
        label: outNet
      });
    });
  });

  // 3. Connect Outputs to Pads
  outputsList.forEach((out) => {
    const producer = cells.find(c => c.outputs.includes(out));
    if (producer) {
      const startX = producer.x + producer.w;
      const startY = producer.y + 40;
      wires.push({
        path: [
          { x: startX, y: startY },
          { x: startX + 30, y: startY },
          { x: startX + 30, y: producer.y + 40 },
          { x: startX + 60, y: producer.y + 40 }
        ],
        layer: 'M1',
        label: out
      });
    }
  });

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  };

  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    if (isDragging) {
      setOffset({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    } else {
      const layoutX = (mouseX - offset.x) / zoom;
      const layoutY = (mouseY - offset.y) / zoom;

      const hovered = cells.find(c => 
        layoutX >= c.x && layoutX <= c.x + c.w &&
        layoutY >= c.y && layoutY <= c.y + c.h
      );
      setHoveredCell(hovered || null);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const scale = e.deltaY < 0 ? 1.1 : 0.9;
    setZoom(prev => Math.min(Math.max(prev * scale, 0.4), 4));
  };

  // Render canvas loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    const isLight = theme === 'light';
    const gridBg = isLight ? '#f8fafc' : '#05080e';
    const gridLine = isLight ? '#e2e8f0' : '#0f172a';
    const textMainColor = isLight ? '#0f172a' : '#ffffff';
    const textMutedColor = isLight ? '#475569' : '#94a3b8';
    const railBg = isLight ? '#cbd5e1' : '#1e293b';
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    ctx.fillStyle = gridBg;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.strokeStyle = gridLine;
    ctx.lineWidth = 1;
    const step = 40 * zoom;
    const startX = offset.x % step;
    const startY = offset.y % step;
    
    for (let x = startX; x < canvas.width; x += step) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }
    for (let y = startY; y < canvas.height; y += step) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }

    ctx.save();
    ctx.translate(offset.x, offset.y);
    ctx.scale(zoom, zoom);

    // Compute layout row bounds to draw rails
    const maxRow = Math.max(0, Math.floor((cells.length - 1) / 4));
    for (let r = 0; r <= maxRow; r++) {
      const railY = 20 + r * 140;
      ctx.fillStyle = railBg;
      ctx.fillRect(10, railY, 480, 15);
      ctx.fillRect(10, railY + 110, 480, 15);
      
      ctx.fillStyle = '#60a5fa';
      ctx.font = '7px sans-serif';
      ctx.fillText('VDD (Power)', 20, railY + 10);
      ctx.fillText('VSS (Ground)', 20, railY + 120);
    }

    // Draw cells
    cells.forEach(cell => {
      const isHovered = hoveredCell && hoveredCell.id === cell.id;
      
      ctx.fillStyle = isHovered ? 'rgba(59, 130, 246, 0.2)' : 'rgba(139, 92, 246, 0.1)';
      ctx.strokeStyle = isHovered ? '#60a5fa' : '#8b5cf6';
      ctx.lineWidth = isHovered ? 2 : 1.5;
      ctx.fillRect(cell.x, cell.y, cell.w, cell.h);
      ctx.strokeRect(cell.x, cell.y, cell.w, cell.h);

      // Draw active gates (silicon strips)
      ctx.fillStyle = 'rgba(16, 185, 129, 0.25)';
      ctx.fillRect(cell.x + 10, cell.y + 5, 6, cell.h - 10);
      ctx.fillRect(cell.x + 24, cell.y + 5, 6, cell.h - 10);
      if (cell.w > 55) {
        ctx.fillRect(cell.x + 38, cell.y + 5, 6, cell.h - 10);
      }

      ctx.fillStyle = textMutedColor;
      ctx.font = '6px monospace';
      ctx.fillText(cell.type, cell.x + 4, cell.y + cell.h - 12);
      
      ctx.fillStyle = textMainColor;
      ctx.font = 'bold 8px sans-serif';
      ctx.fillText(cell.name, cell.x + 4, cell.y + 16);
    });

    // Draw routing wires
    wires.forEach(wire => {
      ctx.strokeStyle = wire.layer === 'M1' ? '#3b82f6' : '#ef4444'; // M1: Blue, M2: Red
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      
      wire.path.forEach((pt, idx) => {
        if (idx === 0) ctx.moveTo(pt.x, pt.y);
        else ctx.lineTo(pt.x, pt.y);
      });
      ctx.stroke();

      // Vias (contacts)
      wire.path.forEach((pt, idx) => {
        if (idx > 0 && idx < wire.path.length - 1) {
          ctx.fillStyle = '#eab308'; // Via Yellow
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, 2, 0, Math.PI * 2);
          ctx.fill();
        }
      });
      
      if (wire.label) {
        ctx.fillStyle = textMutedColor;
        ctx.font = '6px monospace';
        ctx.fillText(wire.label, wire.path[0].x + 4, wire.path[0].y - 3);
      }
    });

    ctx.restore();
  }, [zoom, offset, hoveredCell, theme, cells, wires]);

  const isLight = theme === 'light';
  const svgBg = isLight ? '#f1f5f9' : '#05080e';
  const portText = isLight ? '#0f172a' : '#ffffff';

  if (cells.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%', 
        backgroundColor: svgBg, 
        color: portText, 
        padding: '20px', 
        alignItems: 'center', 
        justifyContent: 'center', 
        textAlign: 'center'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '24px' }}>📐</span>
          <strong style={{ fontSize: '13px', color: portText }}>Place &amp; Route Visualizer Not Available</strong>
          <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#64748b', maxWidth: '320px' }}>
            Write valid inputs and logic gate configurations in Verilog to dynamically view physical cell layout rows.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: 'var(--console-bg)', position: 'relative' }}>
      {/* Visualizer header controls */}
      <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'var(--header-bg)', fontSize: '11px' }}>
        <div style={{ display: 'flex', gap: '16px', color: 'var(--text-muted)' }}>
          <span><strong>Physical Placement &amp; Route Site</strong> (SkyWater 130nm PDK)</span>
          <span>Zoom: {Math.round(zoom * 100)}%</span>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button 
            onClick={() => { setZoom(1); setOffset({ x: 40, y: 40 }); }}
            style={{ padding: '2px 8px', background: 'var(--border-color)', border: 'none', color: 'var(--text-main)', borderRadius: '4px', cursor: 'pointer', fontSize: '10px' }}
          >
            Reset View
          </button>
        </div>
      </div>

      {/* Main Canvas view area */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <canvas
          ref={canvasRef}
          width={700}
          height={380}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
          style={{ display: 'block', width: '100%', height: '100%', cursor: isDragging ? 'grabbing' : 'grab' }}
        />

        {/* Floating Cell Details panel */}
        {hoveredCell && (
          <div style={{ position: 'absolute', bottom: '16px', left: '16px', background: theme === 'light' ? 'rgba(241, 245, 249, 0.95)' : 'rgba(11, 15, 25, 0.9)', border: '1px solid var(--primary-color)', borderRadius: '8px', padding: '10px 14px', width: '200px', fontSize: '11px', color: 'var(--text-main)', boxShadow: theme === 'light' ? '0 4px 20px rgba(0,0,0,0.1)' : '0 4px 20px rgba(0,0,0,0.5)', pointerEvents: 'none' }}>
            <div style={{ fontWeight: 'bold', color: 'var(--primary-color)', borderBottom: '1px solid var(--border-color)', paddingBottom: '4px', marginBottom: '6px' }}>
              {hoveredCell.name}
            </div>
            <div><strong>Cell Model:</strong> {hoveredCell.type}</div>
            <div><strong>Gate Width:</strong> {hoveredCell.w}λ</div>
            <div><strong>Gate Height:</strong> {hoveredCell.h}λ</div>
            <div><strong>Inputs:</strong> {hoveredCell.inputs.join(', ')}</div>
            <div><strong>Outputs:</strong> {hoveredCell.outputs.join(', ')}</div>
          </div>
        )}

        {/* Legend Overlay */}
        <div style={{ position: 'absolute', top: '16px', right: '16px', background: theme === 'light' ? 'rgba(241, 245, 249, 0.9)' : 'rgba(11, 15, 25, 0.85)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '8px 12px', fontSize: '10px', color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', gap: '4px', pointerEvents: 'none' }}>
          <div style={{ fontWeight: 'bold', color: 'var(--text-main)', marginBottom: '4px' }}>Layers Legend</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '12px', height: '6px', background: '#3b82f6', borderRadius: '2px' }}></span> Metal 1 (Horizontal)
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '12px', height: '6px', background: '#ef4444', borderRadius: '2px' }}></span> Metal 2 (Vertical)
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '12px', height: '6px', background: '#8b5cf6', borderRadius: '2px' }}></span> Active Area (Silicon)
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '6px', height: '6px', background: '#eab308', borderRadius: '50%' }}></span> Via (Contact)
          </div>
        </div>
      </div>
    </div>
  );
};

export default PnRVisualizer;
